import { QRCodeSVG } from 'qrcode.react';
import { getCustomerBasedProfile } from 'app/main/apps/common/AuraFunctions';
import { customerText, fitText, formatBoxCode, orderNumberText, qrValue, vasText } from './boxLabel';

/**
 * PGSS-5748: one 4" × 6" label per box, one per printed page. Agreed with PSG on 18 Oct 2026, then (19 Oct) everything
 * the label printed before is back in its own labelled cell:
 * - the company name and address, and the customer's code at the top (the supplier prints this label, so never the
 *   customer's name);
 * - colour, shape and finish; length, thickness and height;
 * - box number, QTY in this box and holes, as before; decedent, order and container numbers;
 * - a QR code of the box code with the code printed under it, for scanning at the supplier, at PSG and onto the truck;
 * - "USA VAS" / "PLANT VAS" at the bottom for an order with VAS.
 */
// A hair under the 6in page: a block of exactly the page height can round past it in Chrome and push the page break
// onto a blank page, which on a thermal roll wastes a label after every box.
const LABEL_STYLE = {
  width: '4in',
  height: '5.98in',
  boxSizing: 'border-box',
  border: '2px solid #000',
  padding: '0.15in',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.08in',
  background: '#fff',
  color: '#000',
  fontFamily: 'Arial, Helvetica, sans-serif',
  // Explicit, not the page's 1.4: every row's height is then known, and the label is sized to fit (review of #2743).
  lineHeight: 1.15,
  overflow: 'hidden',
  pageBreakInside: 'avoid',
  breakInside: 'avoid',
};

const CELL = { border: '2px solid #000', padding: '0.04in 0.06in', textAlign: 'center' };
const CELL_HEAD = { fontWeight: 700, fontSize: '10px', lineHeight: 1.1, textTransform: 'uppercase', letterSpacing: '0.04em' };
/** Text that never wraps past `lines` lines, so a long value can't push the bottom of the label off the page. */
const clamp = (lines) => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  overflow: 'hidden',
  overflowWrap: 'anywhere',
});
const CELL_VALUE = { fontWeight: 700, lineHeight: 1.15 };
/** The font size and line limit for a value that shrinks to fit (see fitText), as a style. */
const fitted = (text, options) => {
  const { fontPx, lines } = fitText(text, options);
  return { fontSize: `${fontPx}px`, ...clamp(lines) };
};
// A third of the label's width holds ~15 bold characters a line at 13px; half of it ~22 at 14px.
const CELL_FIT = { basePx: 13, charsPerLine: 15, lines: 2 };
const DECEDENT_FIT = { basePx: 14, charsPerLine: 22, lines: 2 };
const ADDRESS_FIT = { basePx: 9, charsPerLine: 42, lines: 2, minPx: 7 };

/** A row of labelled cells; a long value (a finish such as "P3/ Polished Back/ BRP/ Serp Top") shrinks to fit. */
function CellRow({ cells, testId }) {
  return (
    <div data-testid={testId} style={{ display: 'grid', gridTemplateColumns: `repeat(${cells.length}, 1fr)`, gap: 0 }}>
      {cells.map(([head, value], index) => (
        <div key={head} style={index === 0 ? CELL : { ...CELL, borderLeft: 'none' }}>
          <div style={CELL_HEAD}>{head}</div>
          <div style={{ ...CELL_VALUE, ...fitted(value, CELL_FIT) }}>{value ?? ''}</div>
        </div>
      ))}
    </div>
  );
}

function BoxLabel({ label, companyName, companyAddress, containerNumber, isPrinting, isLast }) {
  const vas = vasText(label?.vas_location);
  const code = qrValue(label);
  return (
    <div
      className="box-label"
      data-testid="box-label"
      style={{
        ...LABEL_STYLE,
        ...(isPrinting ? { pageBreakAfter: isLast ? 'auto' : 'always', breakAfter: isLast ? 'auto' : 'page', border: 'none' } : {}),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.08in' }}>
        <img src={getCustomerBasedProfile.companyLogo} alt={companyName || 'logo'} style={{ height: '0.28in', objectFit: 'contain' }} />
        <div data-testid="box-label-company" style={{ flex: 1, minWidth: 0, textAlign: 'center', lineHeight: 1.15 }}>
          <div style={{ fontSize: '12px', fontWeight: 800, ...clamp(1) }}>{companyName || ''}</div>
          <div data-testid="box-label-company-address" style={fitted(companyAddress, ADDRESS_FIT)}>{companyAddress || ''}</div>
        </div>
        {/* PGSS-5748 part 2b: a staff print from an order can span containers, so each label may carry its own. */}
        <div data-testid="box-label-container" style={{ fontSize: '12px', fontWeight: 700, textAlign: 'right' }}>
          {label?.container_number || containerNumber || ''}
        </div>
      </div>

      <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: '0.04in' }}>
        <div style={CELL_HEAD}>Customer</div>
        <div data-testid="box-label-customer" style={{ fontSize: '30px', fontWeight: 800, lineHeight: 1.1 }}>
          {customerText(label)}
        </div>
      </div>

      {/* Everything the label printed before the box label (#2739), each in its own labelled cell. */}
      <CellRow
        testId="box-label-stone"
        cells={[['Color', label?.color], ['Shape', label?.type], ['Finish', label?.polish]]}
      />
      <CellRow
        testId="box-label-size"
        cells={[['LEN', label?.length], ['THK', label?.width], ['HGT', label?.height]]}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 0 }}>
        <div style={CELL}>
          <div style={CELL_HEAD}>Box</div>
          <div data-testid="box-label-box" style={{ fontSize: '26px', fontWeight: 800 }}>{label?.box_number}</div>
        </div>
        <div style={{ ...CELL, borderLeft: 'none' }}>
          <div style={CELL_HEAD}>QTY</div>
          <div style={{ fontSize: '20px', fontWeight: 700 }}>{label?.quantity}</div>
        </div>
        <div style={{ ...CELL, borderLeft: 'none' }}>
          <div style={CELL_HEAD}>Holes</div>
          <div style={{ fontSize: '20px', fontWeight: 700 }}>Nil</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
        <div style={CELL}>
          <div style={CELL_HEAD}>Decedent</div>
          {/* The decedent is what a crew checks the box against, so a long name shrinks to fit rather than being cut. */}
          <div
            data-testid="box-label-decedent"
            style={{ lineHeight: 1.2, ...fitted(label?.decedents, DECEDENT_FIT) }}
          >
            {label?.decedents}
          </div>
        </div>
        <div style={{ ...CELL, borderLeft: 'none' }}>
          <div style={CELL_HEAD}>Order No.</div>
          <div data-testid="box-label-order" style={{ fontSize: '14px' }}>{orderNumberText(label)}</div>
        </div>
      </div>

      {/* The QR code takes the space left and shrinks if the rows above run long (down to about 0.9in, still easy to
          scan), so the code under it and the VAS bar are never cut off. */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.04in' }}>
        {code ? (
          <>
            <QRCodeSVG
              value={code}
              size={128}
              level="M"
              marginSize={4}
              title={`Box code ${formatBoxCode(code)}`}
              data-testid="box-label-qr"
              style={{ flex: '0 1 auto', minHeight: 0, height: 'auto', maxHeight: '100%', width: 'auto', maxWidth: '128px' }}
            />
            <div data-testid="box-label-code" style={{ flexShrink: 0, fontFamily: 'Courier New, monospace', fontSize: '18px', fontWeight: 700, letterSpacing: '0.08em' }}>
              {formatBoxCode(code)}
            </div>
          </>
        ) : (
          <div style={{ fontSize: '12px' }}>No box code</div>
        )}
      </div>

      {vas && (
        <div
          data-testid="box-label-vas"
          style={{ flexShrink: 0, background: '#000', color: '#fff', textAlign: 'center', fontSize: '22px', fontWeight: 800, padding: '0.04in', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
        >
          {vas}
        </div>
      )}
    </div>
  );
}

function ShipmentLabelContent({ shippingLabel, labelRef, isPrinting }) {
  const labels = shippingLabel?.shipping_label?.orders || [];
  const companyName = shippingLabel?.shipping_label?.company_name;
  const companyAddress = shippingLabel?.shipping_label?.company_address;
  const containerNumber = shippingLabel?.shipping_label?.container_number;
  return (
    <div
      className="shipping-label"
      ref={labelRef}
      style={isPrinting ? {} : { display: 'flex', flexWrap: 'wrap', gap: '24px', padding: '24px' }}
    >
      {labels.map((label, index) => (
        <BoxLabel
          // The index keeps keys unique: a repeated box number ("3,3") is two labels with one code.
          key={`${label.order_id || label.orderId || ''}-${label.box_number || ''}-${label.box_code || ''}-${index}`}
          label={label}
          companyName={companyName}
          companyAddress={companyAddress}
          containerNumber={containerNumber}
          isPrinting={isPrinting}
          isLast={index === labels.length - 1}
        />
      ))}
    </div>
  );
}

export default ShipmentLabelContent;
